import {
    AccountCategory,
    type AccountSummary,
    type HolisticCategory,
    Provider,
} from '@fruitful/types';
import { useAtomValue } from 'jotai/index';
import React, { useEffect, useMemo, useState } from 'react';
import {
    BodySmallRegular,
    CheckboxWithLabel,
    DollarInput,
    H6Emphasized,
    Input,
    LabelRegular,
    MultilineInput,
    Picker,
    ScrollView,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../../common/components/GroveButtons';
import { GroveSimpleModal } from '../../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../../common/components/Toasts';
import { selectedMembershipAtom } from '../../entityAtoms';
import { useUpdateManualAccountAndRefetch } from '../hooks/externalAccounts/useUpdateManualAccount';
import { useUpsertOrionAccountAndRefetch } from '../hooks/externalAccounts/useUpsertOrionAccount';
import { useUpsertQuilttAccountAndRefetch } from '../hooks/externalAccounts/useUpsertQuilttAccount';
import { useUpsertSpinwheelCreditCardAccountAndRefetch } from '../hooks/externalAccounts/useUpsertSpinwheelCreditCardAccount';
import { useUpsertSpinwheelLoanAccountAndRefetch } from '../hooks/externalAccounts/useUpsertSpinwheelLoanAccount';
import { useUpsertSyncteraAccountAndRefetch } from '../hooks/externalAccounts/useUpsertSyncteraAccount';
import {
    centsToDollarInputDisplayString,
    getAccountCategoryPickerOptions,
    GROVE_WIDE_MODAL_MAX_WIDTH_PX,
    parseDollarInputStringToCents,
} from './accountModalFormUtils';

export const AccountEditModal = ({
    account,
    isOpen,
    onClose,
}: {
    account: AccountSummary;
    isOpen: boolean;
    onClose: () => void;
}) => {
    const categoryOptions = useMemo(
        () => getAccountCategoryPickerOptions(),
        [],
    );

    const resetForm = () => {
        setName(account.name ?? '');
        setAmountStr(
            centsToDollarInputDisplayString(account.currentBalanceCents ?? 0),
        );
        setDescription(account.description ?? '');
        setMinimumPaymentStr(
            centsToDollarInputDisplayString(account.minimumPayment ?? 0),
        );
        setInterestRateStr(
            account.interestRate != null && account.interestRate > 0
                ? String(account.interestRate)
                : '',
        );
        setHolisticCategory(account.category ?? AccountCategory.SAVE);
        setIsHidden(account.isHidden ?? false);
    };

    const [minimumPaymentStr, setMinimumPaymentStr] = useState(
        centsToDollarInputDisplayString(account?.minimumPayment ?? 0),
    );

    const [name, setName] = useState(account.name ?? '');
    const [amountStr, setAmountStr] = useState(() =>
        centsToDollarInputDisplayString(account.currentBalanceCents ?? 0),
    );
    const [description, setDescription] = useState(account.description ?? '');
    const [holisticCategory, setHolisticCategory] = useState<AccountCategory>(
        account.category ?? AccountCategory.SAVE,
    );
    const [interestRateStr, setInterestRateStr] = useState(
        account.interestRate != null && account.interestRate > 0
            ? String(account.interestRate)
            : '',
    );
    const [isHidden, setIsHidden] = useState(account.isHidden ?? false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{
        name?: string;
    }>({});

    useEffect(() => {
        resetForm();
    }, [isOpen]);

    const toast = useToastController();
    const membership = useAtomValue(selectedMembershipAtom);
    const { upsertQuilttAccountAsync } = useUpsertQuilttAccountAndRefetch(
        membership?.id ?? null,
    );
    const { updateManualAccountAsync } = useUpdateManualAccountAndRefetch(
        membership?.id ?? null,
    );
    const { upsertOrionAccountAsync } = useUpsertOrionAccountAndRefetch(
        membership?.id ?? null,
    );
    const { upsertSyncteraAccountAsync } = useUpsertSyncteraAccountAndRefetch(
        membership?.id ?? null,
    );
    const { upsertSpinwheelCreditCardAccountAsync } =
        useUpsertSpinwheelCreditCardAccountAndRefetch(membership?.id ?? null);
    const { upsertSpinwheelLoanAccountAsync } =
        useUpsertSpinwheelLoanAccountAndRefetch(membership?.id ?? null);

    const parseInterestRate = (): number => {
        const t = interestRateStr.trim();
        if (t === '') {
            return 0;
        }
        const n = Number.parseFloat(t);
        return Number.isNaN(n) ? 0 : n;
    };

    const handleSave = async () => {
        if (account?.userId == null) {
            throw new Error('Need user ID to save account updates');
        }

        const errors: typeof validationErrors = {};
        if (!name.trim()) {
            errors.name = 'Name is required.';
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        setShowSpinner(true);

        try {
            const interestRate = parseInterestRate();

            if (account.provider === Provider.MANUAL) {
                const amountCents = parseDollarInputStringToCents(amountStr);
                if (amountCents == null) {
                    toast.show(
                        'Enter a valid balance',
                        MutationErrorToastOptions(),
                    );
                    setShowSpinner(false);
                    return;
                }
                const minCents =
                    parseDollarInputStringToCents(minimumPaymentStr);
                if (minCents == null) {
                    toast.show(
                        'Enter a valid minimum payment',
                        MutationErrorToastOptions(),
                    );
                    setShowSpinner(false);
                    return;
                }
                await updateManualAccountAsync({
                    id: account.id,
                    userId: account.userId,
                    name,
                    amount: amountCents,
                    description,
                    interestRate,
                    minimumPayment: minCents,
                    holisticCategory: holisticCategory.toUpperCase(),
                });
            } else if (account.provider === Provider.QUILTT) {
                const minCents =
                    parseDollarInputStringToCents(minimumPaymentStr);
                if (minCents == null) {
                    toast.show(
                        'Enter a valid minimum payment',
                        MutationErrorToastOptions(),
                    );
                    setShowSpinner(false);
                    return;
                }
                await upsertQuilttAccountAsync({
                    id: account.id,
                    userId: account.userId,
                    name,
                    description,
                    interestRate,
                    minimumPayment: minCents,
                    holisticCategory: holisticCategory.toUpperCase(),
                });
            } else if (account.provider === Provider.ORION) {
                await upsertOrionAccountAsync({
                    id: account.id,
                    userId: account.userId,
                    name,
                    holisticCategory: holisticCategory.toUpperCase(),
                });
            } else if (account.provider === Provider.SYNCTERA) {
                await upsertSyncteraAccountAsync({
                    id: account.id,
                    name,
                    holisticCategory: holisticCategory.toUpperCase(),
                });
            } else if (account.provider === Provider.SPINWHEEL) {
                const isCreditAccount =
                    account.type?.toLowerCase() === 'credit';
                const holisticCategoryValue =
                    holisticCategory.toUpperCase() as HolisticCategory;
                const minCents =
                    parseDollarInputStringToCents(minimumPaymentStr);
                if (minCents == null) {
                    toast.show(
                        'Enter a valid minimum payment',
                        MutationErrorToastOptions(),
                    );
                    setShowSpinner(false);
                    return;
                }
                const upsertAccountBody = {
                    id: account.id,
                    displayName: name,
                    ...(account.userId && { userId: account.userId }),
                    holisticCategory: holisticCategoryValue,
                    minimumPaymentAmountCents: minCents,
                    interestRate: interestRate > 0 ? interestRate : null,
                    description: description ?? '',
                    isHidden,
                };

                if (isCreditAccount) {
                    await upsertSpinwheelCreditCardAccountAsync(
                        upsertAccountBody,
                    );
                } else {
                    await upsertSpinwheelLoanAccountAsync(upsertAccountBody);
                }
            }

            onClose();

            toast.show('Account updated', MutationSuccessToastOptions);
        } catch {
            toast.show('Error updating account', MutationErrorToastOptions());
        } finally {
            setShowSpinner(false);
        }
    };

    const inputsDisabled = showSpinner;

    return (
        <GroveSimpleModal
            isOpen={isOpen}
            onClose={onClose}
            hasCloseButton
            dialogProps={{
                width: GROVE_WIDE_MODAL_MAX_WIDTH_PX,
                maxWidth: '90vw',
            }}
        >
            <YStack
                width="100%"
                minWidth={320}
                maxWidth={GROVE_WIDE_MODAL_MAX_WIDTH_PX}
                maxHeight="80vh"
                flex={1}
                overflow="hidden"
            >
                <ScrollView flex={1} minHeight={0}>
                    <YStack gap={24} padding={24} paddingBottom={12}>
                        <H6Emphasized color="$gray1200">
                            Edit account
                        </H6Emphasized>

                        <YStack gap={8}>
                            <LabelRegular color="$gray1000">
                                Account nickname
                            </LabelRegular>
                            <Input
                                size="regular"
                                value={name}
                                onChangeText={(t) => {
                                    setName(t);
                                    setValidationErrors((v) => ({
                                        ...v,
                                        name: undefined,
                                    }));
                                }}
                                disabled={inputsDisabled}
                            />
                            {validationErrors.name != null ? (
                                <BodySmallRegular color="$red900">
                                    {validationErrors.name}
                                </BodySmallRegular>
                            ) : null}
                        </YStack>

                        <YStack gap={8}>
                            <LabelRegular color="$gray1000">
                                Holistic category
                            </LabelRegular>
                            <Picker
                                size="regular"
                                value={holisticCategory}
                                options={categoryOptions}
                                disabled={inputsDisabled}
                                onChange={(v) => {
                                    setHolisticCategory(v);
                                }}
                            />
                        </YStack>

                        {account.provider === Provider.MANUAL ? (
                            <YStack gap={8}>
                                <LabelRegular color="$gray1000">
                                    Balance
                                </LabelRegular>
                                <DollarInput
                                    size="regular"
                                    value={amountStr}
                                    onChangeText={setAmountStr}
                                    allowDecimals={false}
                                    disabled={inputsDisabled}
                                />
                            </YStack>
                        ) : null}

                        {[
                            Provider.MANUAL,
                            Provider.QUILTT,
                            Provider.SPINWHEEL,
                        ].includes(account.provider) ? (
                            <YStack gap={8}>
                                <LabelRegular color="$gray1000">
                                    Description
                                </LabelRegular>
                                <MultilineInput
                                    value={description}
                                    onChangeText={setDescription}
                                    disabled={inputsDisabled}
                                    numberOfLines={4}
                                    placeholder="Description"
                                />
                            </YStack>
                        ) : null}

                        {[
                            Provider.MANUAL,
                            Provider.QUILTT,
                            Provider.SPINWHEEL,
                        ].includes(account.provider) ? (
                            <YStack gap={8}>
                                <LabelRegular color="$gray1000">
                                    Minimum payment
                                </LabelRegular>
                                <DollarInput
                                    size="regular"
                                    value={minimumPaymentStr}
                                    onChangeText={setMinimumPaymentStr}
                                    allowDecimals={false}
                                    disabled={inputsDisabled}
                                />
                            </YStack>
                        ) : null}
                        {[
                            Provider.MANUAL,
                            Provider.QUILTT,
                            Provider.SPINWHEEL,
                        ].includes(account.provider) ? (
                            <YStack gap={8}>
                                <LabelRegular color="$gray1000">
                                    Interest rate (%)
                                </LabelRegular>
                                <Input
                                    size="regular"
                                    value={interestRateStr}
                                    onChangeText={setInterestRateStr}
                                    keyboardType="decimal-pad"
                                    disabled={inputsDisabled}
                                    placeholder="0"
                                />
                            </YStack>
                        ) : null}

                        {account.provider === Provider.SPINWHEEL ? (
                            <CheckboxWithLabel
                                id="spinwheel-account-hidden"
                                label="Hidden"
                                checked={isHidden}
                                disabled={inputsDisabled}
                                onPress={() => {
                                    setIsHidden((prev) => !prev);
                                }}
                            />
                        ) : null}
                    </YStack>
                </ScrollView>

                <XStack
                    justifyContent="flex-end"
                    alignItems="center"
                    gap={12}
                    paddingHorizontal={24}
                    paddingVertical={16}
                    borderTopWidth={1}
                    borderTopColor="$gray200"
                    flexShrink={0}
                    backgroundColor="$white"
                >
                    {showSpinner ? <Spinner /> : null}
                    <Button
                        variant="tertiary"
                        disabled={inputsDisabled}
                        onPress={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        disabled={inputsDisabled}
                        onPress={() => {
                            void handleSave();
                        }}
                    >
                        Save
                    </Button>
                </XStack>
            </YStack>
        </GroveSimpleModal>
    );
};
